import * as React from "react";
import {
  Box,
  Stack,
  Typography,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import AdminShell, { panelSx, selectMenuProps, selectFieldSx } from "./AdminShell";
import { getSession } from "../../api/session";
import { adminListProfiles, adminSetRole, adminDeleteParticipant } from "../../api/queries";
import type { AdminProfileRow } from "../../types/admin";

const ROLE_STYLE: Record<"admin" | "user", { label: string; icon: React.ElementType; color: string; bg: string; border: string }> = {
  admin: { label: "Admin", icon: ShieldRoundedIcon, color: "#FBBF24", bg: "rgba(245,158,11,0.16)", border: "rgba(245,158,11,0.4)" },
  user: { label: "User", icon: PersonRoundedIcon, color: "#93C5FD", bg: "rgba(59,130,246,0.14)", border: "rgba(59,130,246,0.35)" },
};

// Workshop / mission / group chips of a participant; muted when there is nothing to show.
const assignmentChipSx = (set: boolean) => ({
  bgcolor: set ? "rgba(168,85,247,0.18)" : "rgba(226,232,240,0.08)",
  color: set ? "#E9D5FF" : "rgba(226,232,240,0.5)",
  fontSize: 11,
});

export default function AdminUsers(props: { handlePageChange: (page: string) => void }) {
  const { handlePageChange } = props;
  const session = React.useMemo(() => getSession(), []);
  const email = session?.email ?? "";

  const [profiles, setProfiles] = React.useState<AdminProfileRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [changingId, setChangingId] = React.useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = React.useState<AdminProfileRow | null>(null);
  const [deleting, setDeleting] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    const { data, error: fetchError } = await adminListProfiles(email);
    setLoading(false);
    if (fetchError) {
      setError(fetchError.message);
      return;
    }
    setProfiles(data ?? []);
  }, [email]);

  React.useEffect(() => {
    load();
  }, [load]);

  const changeRole = async (profileId: string, role: "admin" | "user") => {
    setError(null);
    setChangingId(profileId);

    const { error: changeError } = await adminSetRole(email, profileId, role);

    setChangingId(null);

    if (changeError) {
      setError(changeError.message);
      return;
    }
    load();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setError(null);

    const { error: deleteError } = await adminDeleteParticipant(email, deleteTarget.profile_id);

    setDeleting(false);
    if (deleteError) {
      setError(deleteError.message);
      return;
    }
    setDeleteTarget(null);
    load();
  };

  return (
    <AdminShell title="Participants" currentPage="AdminUsers" handlePageChange={handlePageChange}>
      {error && (
        <Alert severity="error" variant="filled" onClose={() => setError(null)} sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress sx={{ color: "#C4B5FD" }} />
        </Stack>
      ) : (
        <Box sx={panelSx}>
          <Stack spacing={1.25}>
            {profiles.length === 0 && (
              <Typography sx={{ color: "rgba(226,232,240,0.5)", fontSize: 13 }}>No registered accounts yet.</Typography>
            )}
            {profiles.map((p) => {
              const roleStyle = ROLE_STYLE[p.role];
              const isYou = !!email && p.profile_email.trim().toLowerCase() === email.trim().toLowerCase();
              return (
                <Stack
                  key={p.profile_id}
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  flexWrap="wrap"
                  rowGap={0.75}
                  sx={{ py: 1, borderBottom: "1px solid rgba(255,255,255,0.08)" }}
                >
                  <Stack sx={{ minWidth: 200, flex: 1 }}>
                    <Typography sx={{ color: "#FFFFFF", fontWeight: 700, fontSize: 13.5 }}>
                      {[p.first_name, p.last_name].filter(Boolean).join(" ") || "—"}
                      {isYou && (
                        <Box component="span" sx={{ ml: 0.75, color: "#C4B5FD", fontWeight: 700, fontSize: 12.5 }}>
                          (you)
                        </Box>
                      )}
                    </Typography>
                    <Typography sx={{ color: "rgba(226,232,240,0.6)", fontSize: 12.5 }}>{p.profile_email}</Typography>
                  </Stack>

                  {p.role === "user" &&
                    (p.workshop_name ? (
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap rowGap={0.5}>
                        <Chip label={`Workshop: ${p.workshop_name}`} size="small" sx={assignmentChipSx(true)} />
                        <Chip
                          label={p.chosen_mission_name ? `Mission: ${p.chosen_mission_name}` : "No mission yet"}
                          size="small"
                          sx={assignmentChipSx(Boolean(p.chosen_mission_name))}
                        />
                        <Chip
                          label={p.group_name ? `Group: ${p.group_name}` : "No group yet"}
                          size="small"
                          title={
                            !p.group_name
                              ? undefined
                              : p.group_unlocked
                              ? "Has entered the group's name and access code"
                              : "Planned into this group; hasn't entered its name and access code yet"
                          }
                          sx={assignmentChipSx(Boolean(p.group_name))}
                        />
                      </Stack>
                    ) : (
                      <Chip label="No workshop chosen" size="small" sx={assignmentChipSx(false)} />
                    ))}

                  <Select
                    size="small"
                    value={p.role}
                    disabled={changingId === p.profile_id}
                    onChange={(e) => changeRole(p.profile_id, e.target.value as "admin" | "user")}
                    renderValue={(value) => {
                      const s = ROLE_STYLE[value as "admin" | "user"];
                      const Icon = s.icon;
                      return (
                        <Stack direction="row" spacing={0.6} alignItems="center">
                          <Icon sx={{ fontSize: 15, color: s.color }} />
                          <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: s.color }}>{s.label}</Typography>
                        </Stack>
                      );
                    }}
                    sx={{
                      ...selectFieldSx,
                      minWidth: 130,
                      bgcolor: roleStyle.bg,
                      borderRadius: 999,
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: roleStyle.border },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: roleStyle.color },
                      "& .MuiSelect-select": { py: 0.6, borderRadius: 999 },
                    }}
                    MenuProps={selectMenuProps}
                  >
                    {(Object.keys(ROLE_STYLE) as Array<"admin" | "user">).map((r) => {
                      const s = ROLE_STYLE[r];
                      const Icon = s.icon;
                      return (
                        <MenuItem key={r} value={r} sx={{ fontSize: 13 }}>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <Icon sx={{ fontSize: 16, color: s.color }} />
                            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{s.label}</Typography>
                          </Stack>
                        </MenuItem>
                      );
                    })}
                  </Select>

                  <IconButton size="small" onClick={() => setDeleteTarget(p)} aria-label="Delete participant">
                    <DeleteOutlineRoundedIcon sx={{ fontSize: 18, color: "rgba(248,113,113,0.75)" }} />
                  </IconButton>
                </Stack>
              );
            })}
          </Stack>
        </Box>
      )}

      <Dialog
        open={!!deleteTarget}
        onClose={() => (!deleting ? setDeleteTarget(null) : undefined)}
        PaperProps={{ sx: { bgcolor: "#2B2E40", border: "1px solid #3E4258", borderRadius: 2.5, minWidth: { xs: "auto", sm: 420 } } }}
      >
        <DialogTitle sx={{ color: "#FFFFFF", fontWeight: 800 }}>Delete this participant?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "rgba(226,232,240,0.75)", fontSize: 13.5 }}>
            This permanently deletes{" "}
            <Box component="span" sx={{ color: "#FFFFFF", fontWeight: 700 }}>
              {deleteTarget ? [deleteTarget.first_name, deleteTarget.last_name].filter(Boolean).join(" ") || deleteTarget.profile_email : ""}
            </Box>{" "}
            ({deleteTarget?.profile_email}) and every trace of their data -- profile, role, chosen mission and workshop access. This
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting} sx={{ textTransform: "none", fontWeight: 700, color: "rgba(226,232,240,0.7)" }}>
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            disabled={deleting}
            variant="contained"
            sx={{ textTransform: "none", fontWeight: 800, bgcolor: "#DC2626", "&:hover": { bgcolor: "#B91C1C" }, borderRadius: 1.5 }}
          >
            {deleting ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : "Delete permanently"}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminShell>
  );
}
